import { Text } from '@driftt/dds'
import React from 'react'

import {
  archivePlaybookById,
  disablePlaybookById,
  enablePlaybookById,
} from 'api/playbooks'
import { formatTime } from 'utils/moment'

import {
  PlaybookActionsCell,
  PlaybookNameCell,
  PlaybookPriorityCell,
  PlaybookReportTypeCell,
  PlaybookStatusCell,
  PlaybookUpdatedAuthorCell,
} from './components/TableRow'

export const makePlaybooksTableColumns = ({
  tableState = 'CHAT_ACTIVE',
  onChange = () => {},
  workspaces,
  orgUsers,
  showWorkspaces,
  cloneAsBlockExclusions,
  enabledPlaybookReportTypes,
  setPlaybookRowModalContext,
  setCloneAsBlockExclusions,
  showTransientNotification,
}) => {
  const [
    priority,
    name,
    workspace,
    status,
    type,
    lastUpdated,
    updatedBy,
    scheduledFor,
    actions,
  ] = [
    {
      header: 'Priority',
      value: 'priority',
      cell: (playbook) => PlaybookPriorityCell(playbook),
      size: 90,
    },
    {
      header: 'Playbook',
      value: 'name',
      cell: (playbook) => PlaybookNameCell(playbook),
      size: 300,
    },
    {
      header: 'Workspace',
      value: 'workspace.name',
      cell: (playbook) => (
        <Text variant="body">{workspaces?.[playbook?.workspaceId]?.name}</Text>
      ),
      size: 116,
    },
    {
      disableSort: true,
      header: 'Status',
      value: 'status',
      size: 70,
      cell: (playbook) => PlaybookStatusCell(playbook),
    },
    {
      disableSort: true,
      header: 'Type',
      value: 'type',
      size: 100,
      cell: (playbook) =>
        tableState === 'FASTLANE_ACTIVE'
          ? 'Fastlane'
          : PlaybookReportTypeCell(playbook),
    },
    {
      header: 'Last updated',
      value: 'last_updated',
      size: 100,
      cell: (playbook) => formatTime(playbook.updatedAt, 'MM/DD/YY'),
    },
    {
      disableSort: true,
      header: 'Updated by',
      value: 'updated_by',
      size: 80,
      cell: (playbook) => PlaybookUpdatedAuthorCell(playbook, orgUsers),
    },
    {
      header: 'Scheduled for',
      value: 'scheduled_for',
      size: 120,
      cell: (playbook) =>
        formatTime(playbook?.targeting?.dueAt, 'MMMM D, YYYY h:m A'),
    },
    {
      id: 'actions',
      render: (playbook) =>
        PlaybookActionsCell({
          callback: onChange,
          canEnableOrDisablePlaybook:
            tableState === 'CHAT_ACTIVE' ||
            tableState === 'MEETING_BOOKING_ACTIVE',
          cloneAsBlockExclusions,
          enabledPlaybookReportTypes,
          playbook,
          setPlaybookRowModalContext,
          setCloneAsBlockExclusions,
          showTransientNotification,
        }),
      size: 60,
    },
  ]

  const maybeWorkspaceColumn = showWorkspaces ? [workspace] : []

  const DEFAULT_COLUMNS = [name, type, lastUpdated, updatedBy, actions]

  const tableStateToColumnMap = {
    CHAT_ACTIVE: [
      priority,
      name,
      ...maybeWorkspaceColumn,
      status,
      type,
      lastUpdated,
      updatedBy,
      actions,
    ],
    CHAT_ARCHIVED: [
      name,
      ...maybeWorkspaceColumn,
      status,
      type,
      lastUpdated,
      updatedBy,
      actions,
    ],
    EMAIL_ACTIVE: [name, status, type, lastUpdated, updatedBy, actions],
    EMAIL_DRAFTS: DEFAULT_COLUMNS,
    EMAIL_SENT: DEFAULT_COLUMNS,
    EMAIL_ARCHIVED: [name, status, type, lastUpdated, updatedBy, actions],
    EMAIL_SCHEDULED: [
      name,
      status,
      type,
      scheduledFor,
      lastUpdated,
      updatedBy,
      actions,
    ],
    FASTLANE_ACTIVE: [
      name,
      ...maybeWorkspaceColumn,
      status,
      type,
      lastUpdated,
      updatedBy,
      actions,
    ],
    FASTLANE_ARCHIVED: [
      name,
      ...maybeWorkspaceColumn,
      status,
      lastUpdated,
      updatedBy,
      actions,
    ],
    SEQUENCE_ACTIVE: [name, status, type, lastUpdated, updatedBy, actions],
    SEQUENCE_DRAFTS: DEFAULT_COLUMNS,
    SEQUENCE_ARCHIVED: [name, status, type, lastUpdated, updatedBy, actions],
    SIGNATURE_ACTIVE: [name, status, type, lastUpdated, updatedBy, actions],
    SIGNATURE_ARCHIVED: [name, status, type, lastUpdated, updatedBy, actions],
    MEETING_BOOKING_ACTIVE: [
      priority,
      name,
      status,
      ...maybeWorkspaceColumn,
      lastUpdated,
      updatedBy,
      actions,
    ],
    MEETING_BOOKING_ARCHIVED: [
      name,
      status,
      ...maybeWorkspaceColumn,
      lastUpdated,
      updatedBy,
      actions,
    ],
  }

  return tableStateToColumnMap[tableState]
}

const DEFAULT_SORT = { property: 'last_updated', dir: 'asc' }

export const PLAYBOOK_EDITOR_TYPES_STATUS_OPTIONS = {
  CHAT: [
    {
      label: 'Active playbooks',
      value: 'CHAT_ACTIVE',
      sortProperties: { property: 'priority', dir: 'asc' },
    },
    {
      label: 'Archived playbooks',
      value: 'CHAT_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  FASTLANE: [
    {
      label: 'Active playbooks',
      value: 'FASTLANE_ACTIVE',
      sortProperties: { property: 'last_updated', dir: 'desc' },
    },
    {
      label: 'Archived playbooks',
      value: 'FASTLANE_ARCHIVED',
      sortProperties: { property: 'last_updated', dir: 'desc' },
    },
  ],
  EMAIL: [
    {
      label: 'Automated playbooks',
      value: 'EMAIL_ACTIVE',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Draft playbooks',
      value: 'EMAIL_DRAFTS',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Sent playbooks',
      value: 'EMAIL_SENT',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Archived playbooks',
      value: 'EMAIL_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Scheduled playbooks',
      value: 'EMAIL_SCHEDULED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  SEQUENCE: [
    {
      label: 'Active sequences',
      value: 'SEQUENCE_ACTIVE',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Draft sequences',
      value: 'SEQUENCE_DRAFTS',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Archived sequences',
      value: 'SEQUENCE_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  SIGNATURE: [
    {
      label: 'Active signatures',
      value: 'SIGNATURE_ACTIVE',
      sortProperties: DEFAULT_SORT,
    },
    {
      label: 'Archived signatures',
      value: 'SIGNATURE_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
  MEETINGS: [
    {
      label: 'Active meetings playbooks',
      value: 'MEETING_BOOKING_ACTIVE',
      sortProperties: { property: 'priority', dir: 'asc' },
    },
    {
      label: 'Archived meetings playbooks',
      value: 'MEETING_BOOKING_ARCHIVED',
      sortProperties: DEFAULT_SORT,
    },
  ],
}

export const SORT_PROPERTIES = {
  PRIORITY: 'priority',
  NAME: 'name',
  LAST_UPDATED: 'last_updated',
}

export const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
}

export const PLAYBOOK_CATEGORIES = {
  CHAT: true,
  EMAIL: true,
  FASTLANE: true,
  SEQUENCE: true,
  SIGNATURE: true,
  MEETINGS: true,
}

export const WORKSPACE_SUPPORTED_PLAYBOOK_TYPES = ['CHAT', 'FASTLANE']

export const TABLE_STATES = {
  CHAT_ACTIVE: 'CHAT_ACTIVE',
  CHAT_ARCHIVED: 'CHAT_ARCHIVED',
  EMAIL_ACTIVE: 'EMAIL_ACTIVE',
  EMAIL_DRAFT: 'EMAIL_DRAFT',
  EMAIL_SENT: 'EMAIL_SENT',
  EMAIL_ARCHIVED: 'EMAIL_ARCHIVED',
  EMAIL_SCHEDULED: 'EMAIL_SCHEDULED',
  FASTLANE_ACTIVE: 'FASTLANE_ACTIVE',
  FASTLANE_ARCHIVED: 'FASTLANE_ARCHIVED',
  SEQUENCE_ACTIVE: 'SEQUENCE_ACTIVE',
  SEQUENCE_ARCHIVED: 'SEQUENCE_ARCHIVED',
  SEQUENCE_DRAFTS: 'SEQUENCE_DRAFTS',
  SIGNATURE_ACTIVE: 'SIGNATURE_ACTIVE',
  SIGNATURE_ARCHIVED: 'SIGNATURE_ARCHIVED',
  MEETING_BOOKING_ACTIVE: 'MEETING_BOOKING_ACTIVE',
  MEETING_BOOKING_ARCHIVED: 'MEETING_BOOKING_ARCHIVED',
}

export const PLAYBOOK_INTERACTION_MODAL = {
  DELETE: 'DELETE',
  CLONE: 'CLONE',
  MOVE: 'MOVE',
  CLONE_AS_CLP: 'CLONE_AS_CLP',
  CLONE_AS_BOT: 'CLONE_AS_BOT',
  CLONE_AS_BLOCK: 'CLONE_AS_BLOCK',
  RESTORE_ARCHIVED_BLOCKS: 'RESTORE_ARCHIVED_BLOCKS',
}

export const PLAYBOOK_CLONE_TYPES = {
  CLP: 'CLP',
  BOT: 'BOT',
  BLOCK: 'BLOCK',
}

export const PLAYBOOK_ACTIONS = {
  ARCHIVE: archivePlaybookById,
  DISABLE: disablePlaybookById,
  ENABLE: enablePlaybookById,
  RESTORE: disablePlaybookById,
}

export const TOAST_NOTIFICATIONS = {
  ARCHIVE: (name) => `${name} archived successfully.`,
  CLONE: (name) => `${name} cloned successfully.`,
  CLONE_AS_BOT: (name) => `${name} cloned successfully.`,
  CLONE_AS_CLP: (name) => `${name} cloned successfully.`,
  DELETE: (name) => `${name} deleted successfully`,
  DISABLE: (name) => `${name} turned off`,
  ENABLE: (name) => `${name} turned on`,
  RESTORE: (name) => `${name} restored successfully`,
}
